import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { WorkspaceContext } from '~/contexts/WorkspaceContext'
import { getWorkspaceMembers } from '~/services/organizationService'
import { WorkspaceMember } from '~/types/ResponseType'

/**
 * Hook to fetch and manage workspace members (team).
 * Includes both workspace members (UserOrgs) and Account Admins (AccountMemberships).
 * Resolves workspaceUuid from URL params first, then falls back to WorkspaceContext
 * (needed for super admin panel where there are no route params).
 */
export function useWorkspaceMembers() {
  const { workspaceUuid: routeUuid } = useParams<{ workspaceUuid: string }>()
  const workspaceCtx = useContext(WorkspaceContext)
  const workspaceUuid = routeUuid || workspaceCtx?.currentWorkspace?.uuid
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchMembers = useCallback(async () => {
    if (!workspaceUuid) {
      setMembers([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await getWorkspaceMembers(workspaceUuid)
      setMembers(data)
    } catch (err) {
      console.error('Failed to fetch workspace members:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch members')
      setMembers([])
    } finally {
      setIsLoading(false)
    }
  }, [workspaceUuid])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  return {
    members,
    isLoading,
    error,
    refetch: fetchMembers
  }
}
